import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/constants';
import { generateToken, TOKEN_HEADER } from '../utils/tokenUtils';
import WorkshopHeader from '../components/workshop/WorkshopHeader';
import WorkshopCard from '../components/workshop/WorkshopCard';
import WorkshopEmptyState from '../components/workshop/WorkshopEmptyState';
import WorkshopSkeleton from '../components/workshop/WorkshopSkeleton';
import WorkshopDetail from './WorkshopDetail';
import '../styles/events.css';

const normalizeWorkshops = (payload) => {
  const items = payload?.workshops || payload?.data || [];
  return Array.isArray(items) ? items : [];
};

const Workshop = () => {
  const navigate = useNavigate();
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);

  const attendeeName = localStorage.getItem('attendeeName') || localStorage.getItem('userName') || 'Attendee';
  const attendeeToken = localStorage.getItem('attendeeToken') || localStorage.getItem('token');

  useEffect(() => {
    const fetchWorkshops = async () => {
      setLoading(true);
      setError('');

      // Login bypass mode: allow opening /workshop without forcing authentication.
      if (!attendeeToken) {
        setWorkshops([]);
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API_ENDPOINTS.BASE_URL}/workshops`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${attendeeToken}`,
            [TOKEN_HEADER]: generateToken(),
          },
        });

        const data = await res.json();
        if (res.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('attendeeToken');
          localStorage.removeItem('attendeeId');
          localStorage.removeItem('userId');
          localStorage.removeItem('userid');
          localStorage.removeItem('userName');
          localStorage.removeItem('attendeeName');
          localStorage.removeItem('userEmail');
          localStorage.removeItem('attendeeEmail');
          setWorkshops([]);
          setError('Session expired. You can still browse this page, but sign in to view assigned workshops.');
          return;
        }
        if (!res.ok) {
          throw new Error(data?.error || data?.message || 'Unable to load your workshops.');
        }

        setWorkshops(normalizeWorkshops(data));
      } catch (err) {
        setError(err.message || 'Unable to load your workshops.');
      } finally {
        setLoading(false);
      }
    };

    fetchWorkshops();
  }, [attendeeToken, navigate]);

  const sortedWorkshops = useMemo(() => {
    return [...workshops].sort((a, b) => {
      const aDate = new Date(a.start_datetime || a.startDate || a.date || 0).getTime();
      const bDate = new Date(b.start_datetime || b.startDate || b.date || 0).getTime();
      return aDate - bDate;
    });
  }, [workshops]);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('attendeeToken');
    localStorage.removeItem('attendeeId');
    localStorage.removeItem('userId');
    localStorage.removeItem('userid');
    localStorage.removeItem('userName');
    localStorage.removeItem('attendeeName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('attendeeEmail');
    navigate('/events/login?returnTo=/workshop');
  };

  if (selectedWorkshop) {
    return (
      <WorkshopDetail
        workshop={selectedWorkshop}
        onBackToWorkshops={() => setSelectedWorkshop(null)}
      />
    );
  }

  return (
    <div className="ep-page wk-page">
      <header className="ep-hero wk-hero">
        <div className="ep-hero-glow" aria-hidden="true" />
        <div className="ep-hero-inner wk-hero-inner">
          <WorkshopHeader
            attendeeName={attendeeName}
            workshopCount={sortedWorkshops.length}
            onBackToEvents={() => navigate('/events')}
            onLogout={logout}
          />
        </div>
      </header>

      <section className="ep-events-section wk-list-section">
        <div className="ep-events-inner">
          {loading && <WorkshopSkeleton />}

          {!loading && error && (
            <div className="ep-empty">
              <div className="ep-empty-icon">⚠️</div>
              <h3>Couldn&apos;t load workshops</h3>
              <p className="ep-muted">{error}</p>
              <button className="ep-btn-primary wk-retry-btn" onClick={() => window.location.reload()}>
                Try again
              </button>
            </div>
          )}

          {!loading && !error && sortedWorkshops.length === 0 && <WorkshopEmptyState />}

          {!loading && !error && sortedWorkshops.length > 0 && (
            <div className="wk-grid">
              {sortedWorkshops.map((workshop) => (
                <WorkshopCard
                  key={workshop.id || workshop.title}
                  workshop={workshop}
                  onOpen={(chosenWorkshop) => setSelectedWorkshop(chosenWorkshop)}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Workshop;